import {
  Warning as WarningIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useState } from 'react';

import { log } from '../utils/errors/logger';
import {
  useGetSystemStatus,
  useCleanupAllData,
  useFixDuplicateACL,
  useRecreateQdrantCollection,
} from '@/hooks/api/config/useServerAdmin';

interface SystemStatus {
  timestamp: string;
  mongodb: {
    status: string;
    collections: Record<string, number | string>;
  };
  qdrant: {
    status: string;
    collections: Record<string, any>;
  };
  s3: {
    bucket_exists?: boolean;
    error?: string;
    bucket_name?: string;
    endpoint_url?: string;
    object_count?: number;
    example_objects?: string[];
  };
  error?: string;
}

const DataManagement: React.FC = () => {
  // const { t } = useTranslation(); // Uncomment when translations are added
  const { data: systemStatus, isLoading, refetch } = useGetSystemStatus();
  const cleanupAllData = useCleanupAllData();
  const fixDuplicateACL = useFixDuplicateACL();
  const recreateQdrant = useRecreateQdrantCollection();

  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; action: () => void }>({
    open: false,
    title: '',
    message: '',
    action: () => {},
  });


  const handleCleanupAll = async () => {
    try {
      await cleanupAllData.mutateAsync();
      setAlert({ type: 'success', message: 'Data cleanup completed' });
    } catch (error) {
      log.error('Error during cleanup', { error });
      setAlert({ type: 'error', message: 'Failed to cleanup data' });
    }
  };

  const handleFixDuplicateACL = async () => {
    try {
      const result = await fixDuplicateACL.mutateAsync();
      setAlert({
        type: 'success',
        message: `Fixed duplicate ACL entries. Removed ${result.duplicates_removed} duplicates.`
      });
    } catch (error) {
      log.error('Error fixing duplicate ACL', { error });
      setAlert({ type: 'error', message: 'Failed to fix duplicate ACL entries' });
    }
  };

  const handleRecreateQdrant = async () => {
    try {
      await recreateQdrant.mutateAsync('smartdoc');
      setAlert({ type: 'success', message: 'Qdrant collection recreated successfully' });
    } catch (error) {
      log.error('Error recreating Qdrant collection', { error });
      setAlert({ type: 'error', message: 'Failed to recreate Qdrant collection' });
    }
  };

  const showConfirmDialog = (title: string, message: string, action: () => void) => {
    setConfirmDialog({ open: true, title, message, action });
  };

  const handleConfirmAction = () => {
    confirmDialog.action();
    setConfirmDialog({ open: false, title: '', message: '', action: () => {} });
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Data Management
      </Typography>
      
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* System Status Section */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">System Status</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>

        {systemStatus ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <StorageIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">MongoDB</Typography>
                    <Chip
                      label={systemStatus.mongodb.status}
                      color={systemStatus.mongodb.status === 'connected' ? 'success' : 'error'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Collection</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(systemStatus.mongodb.collections).map(([name, count]) => (
                          <TableRow key={name}>
                            <TableCell>{name}</TableCell>
                            <TableCell align="right">{count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <StorageIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Qdrant</Typography>
                    <Chip
                      label={systemStatus.qdrant.status}
                      color={systemStatus.qdrant.status === 'connected' ? 'success' : 'error'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Collection</TableCell>
                          <TableCell align="right">Vectors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(systemStatus.qdrant.collections).map(([name, info]) => (
                          <TableRow key={name}>
                            <TableCell>{name}</TableCell>
                            <TableCell align="right">
                              {typeof info === 'object' ? info.vectors_count : info}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <StorageIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">S3 Storage</Typography>
                    <Chip
                      label={systemStatus.s3.bucket_exists ? 'Available' : 'Error'}
                      color={systemStatus.s3.bucket_exists ? 'success' : 'error'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  
                  {systemStatus.s3.bucket_name && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Bucket:</strong> {systemStatus.s3.bucket_name}
                    </Typography>
                  )}
                  
                  {systemStatus.s3.endpoint_url && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Endpoint:</strong> {systemStatus.s3.endpoint_url}
                    </Typography>
                  )}
                  
                  {systemStatus.s3.object_count !== undefined && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Objects:</strong> {systemStatus.s3.object_count}
                    </Typography>
                  )}
                  
                  {systemStatus.s3.example_objects && systemStatus.s3.example_objects.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Example files:</strong>
                      </Typography>
                      {systemStatus.s3.example_objects.map((obj, index) => (
                        <Typography key={index} variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                          {obj}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  
                  {systemStatus.s3.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {systemStatus.s3.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="text.secondary">
              {isLoading ? 'Loading system status...' : 'No system status data available'}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Data Operations Section */}
      <Typography variant="h5" mb={2}>Data Operations</Typography>
      
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Fix Issues
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fix common data issues and inconsistencies.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleFixDuplicateACL}
                disabled={fixDuplicateACL.isPending || isLoading}
                sx={{ mr: 1, mb: 1 }}
              >
                Fix Duplicate ACL Entries
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => showConfirmDialog(
                  'Recreate Qdrant Collection',
                  'This will delete all vector data and recreate the collection. Are you sure?',
                  handleRecreateQdrant
                )}
                disabled={recreateQdrant.isPending || isLoading}
                sx={{ mb: 1 }}
              >
                Recreate Qdrant Collection
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="error" />
                Danger Zone
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Destructive operations that cannot be undone.
              </Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => showConfirmDialog(
                  'Delete All Data',
                  'This will permanently delete all documents, groups, ACL entries, audit logs, and S3 files. This action cannot be undone!',
                  handleCleanupAll
                )}
                disabled={cleanupAllData.isPending || isLoading}
              >
                Delete All Data (MongoDB + Qdrant + S3)
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {(isLoading || cleanupAllData.isPending || fixDuplicateACL.isPending || recreateQdrant.isPending) && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataManagement;